import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha,
  Fade,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Google from '@mui/icons-material/Google';
import Microsoft from '@mui/icons-material/Microsoft';
import LinkOff from '@mui/icons-material/LinkOff';
import CheckCircle from '@mui/icons-material/CheckCircle';
import {
  ConnectedCalendar,
  OnboardingStepProps,
} from '@common/components/lib/partials/onboarding/defs/types';
import GoogleCalendarFAQ from '@modules/help/components/GoogleCalendarFAQ';

interface CalendarStepProps extends OnboardingStepProps {
  connectedCalendars: ConnectedCalendar[];
  onConnectCalendar: (provider: 'google' | 'microsoft') => Promise<void>;
  onDisconnectCalendar: (provider: string) => Promise<void>;
  onSkip?: () => void;
  isFirstTime?: boolean;
  reconnectErrorMessage?: string | null;
}

const CalendarStep = ({
  onBack,
  isLoading,
  connectedCalendars,
  onConnectCalendar,
  onDisconnectCalendar,
  onSkip,
  isFirstTime = true,
  reconnectErrorMessage = null,
}: CalendarStepProps) => {
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return <Google sx={{ color: '#4285F4', fontSize: { xs: 18, sm: 20 } }} />;
      case 'microsoft':
        return <Microsoft sx={{ color: '#0078D4', fontSize: { xs: 18, sm: 20 } }} />;
      default:
        return null;
    }
  };

  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Google Agenda';
      case 'microsoft':
        return 'Microsoft Calendar';
      default:
        return provider;
    }
  };

  const hasAnyConnectedCalendar = useCallback(() => {
    return connectedCalendars.some((calendar) => calendar.isActive);
  }, [connectedCalendars]);

  const handleConnectCalendar = async (provider: 'google' | 'microsoft') => {
    if (hasAnyConnectedCalendar()) {
      return;
    }

    try {
      setConnectingProvider(provider);
      setShowError(false);
      await onConnectCalendar(provider);
    } catch (error) {
      console.error('Error connecting calendar:', error);
    } finally {
      setConnectingProvider(null);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '600px',
          mx: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, width: '100%' }}>
          {onBack && (
            <IconButton onClick={onBack} disabled={isLoading}>
              <ArrowBack />
            </IconButton>
          )}
          {isFirstTime ? (
            <>
              <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem', mb: 0.5 }}
                >
                  Dernière étape
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}
                >
                  Je connecte un agenda
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, mt: 0.5 }}
                >
                  Pour que l'on ne prenne pas de RDV sur un créneau déjà occupé. Cela prend 30s.
                </Typography>
              </Box>
              <Box sx={{ width: 30 }} />
            </>
          ) : (
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', mb: 0.5 }}
              >
                Oups ! Un problème est survenu
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                color="primary"
                sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}
              >
                Reconnectez votre agenda
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, mt: 0.5 }}
              >
                Pour que l'on ne prenne pas de RDV sur un créneau déjà occupé. Cela prend 30s.
              </Typography>
              {reconnectErrorMessage && (
                <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                  {reconnectErrorMessage}
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {connectedCalendars && connectedCalendars.length > 0 && (
          <List sx={{ p: 0, mb: 2, width: '100%' }}>
            {connectedCalendars.map((calendar) => (
              <ListItem
                key={calendar.id}
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1.5,
                  backgroundColor: '#f9fafb',
                  borderRadius: 1,
                  mb: 1,
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                    borderColor: '#d1d5db',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getProviderIcon(calendar.provider)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {getProviderLabel(calendar.provider)}
                      </Typography>
                    }
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        {(calendar.settings?.provider_email || calendar.email) && (
                          <>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {calendar.settings?.provider_email || calendar.email}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <CheckCircle sx={{ fontSize: 14, color: '#10b981' }} />
                              <Typography variant="caption" sx={{ color: '#10b981', ml: 0.5 }}>
                                Connecté
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Stack>
                    }
                  />
                </Box>
                <IconButton
                  onClick={() => onDisconnectCalendar(calendar.provider)}
                  size="small"
                  sx={{
                    color: '#6b7280',
                    '&:hover': {
                      backgroundColor: alpha('#ef4444', 0.08),
                      color: '#ef4444',
                    },
                  }}
                >
                  <LinkOff fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}

        {showError && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            Veuillez connecter un calendrier pour continuer.
          </Alert>
        )}

        {!hasAnyConnectedCalendar() && (
          <Stack spacing={1.5} sx={{ width: '100%', marginBottom: 4 }}>
            <Button
              startIcon={
                connectingProvider === 'google' ? <CircularProgress size={16} /> : <Google />
              }
              onClick={() => handleConnectCalendar('google')}
              disabled={connectingProvider !== null}
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                py: { xs: 0.875, sm: 1 },
                textTransform: 'none',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                fontWeight: 500,
                transition: 'all 0.2s ease',
                borderColor: '#4285F4',
                color: '#4285F4',
                '&:hover': {
                  backgroundColor: alpha('#4285F4', 0.04),
                  borderColor: '#4285F4',
                  color: '#4285F4',
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {connectingProvider === 'google' ? 'Connexion...' : 'Connecter mon agenda Google'}
            </Button>

            {onSkip && (
              <Button
                onClick={onSkip}
                variant="text"
                fullWidth
                size="small"
                sx={{
                  py: 1,
                  textTransform: 'none',
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  fontWeight: 500,
                  color: '#6b7280',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#374151',
                  },
                }}
              >
                Passer cette étape
              </Button>
            )}

            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1.5 }}>
              <Divider sx={{ flex: 1, borderColor: '#d1d5db' }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontWeight: 500,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: 1,
                }}
              >
                OU
              </Typography>
              <Divider sx={{ flex: 1, borderColor: '#d1d5db' }} />
            </Box>

            <Button
              startIcon={
                connectingProvider === 'microsoft' ? <CircularProgress size={16} /> : <Email />
              }
              onClick={() => handleConnectCalendar('microsoft')}
              disabled={connectingProvider !== null}
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                py: { xs: 0.875, sm: 1 },
                textTransform: 'none',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                borderColor: '#e5e7eb',
                color: '#374151',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#0078D4',
                  backgroundColor: alpha('#0078D4', 0.04),
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {connectingProvider === 'microsoft' ? 'Connexion...' : 'Connecter un agenda Outlook'}
            </Button> */}
          </Stack>
        )}

        <GoogleCalendarFAQ />
      </Box>
    </Fade>
  );
};

export default CalendarStep;
